import { useEffect, useRef, useState } from 'react';
import * as Y from 'yjs';
import { HocuspocusProvider } from '@hocuspocus/provider';
import { IndexeddbPersistence } from 'y-indexeddb';
import {
  applyCreateNodeTransaction,
  applyDeleteSubtreeTransaction,
  applyMoveNodesTransaction,
  applyReparentTransaction,
  stripTransientNodeFields,
} from '../../utils/mindmap/yjsTransactions';
import { createYjsHistoryActions } from '../../utils/mindmap/yjsHistory';
import {
  createAwarenessState,
  filterAwarenessStates,
} from '../../utils/mindmap/awareness';
import {
  registerOfflineYjsDatabase,
} from '../../utils/mindmap/offlinePersistence';
import {
  buildCollaborationCredential,
  getShareToken,
} from '../../utils/mindmap/apiClient';

const getDefaultWsUrl = () => 'ws://localhost:4000/ws';
const relKey = (parentId, childId) => `${parentId}:${childId}`;

function buildSnapshot(yNodes, yRels, yMeta) {
  const nodesJson = yNodes.toJSON();
  const relsJson = yRels.toJSON();
  const metaJson = yMeta.toJSON();

  return {
    nodes: Object.entries(nodesJson).map(([id, data]) => ({ id, ...data })),
    relationships: Object.values(relsJson),
    root: metaJson.root || null,
    meta: metaJson,
  };
}

export function useYjsMap({
  mindmapId,
  initialData,
  authToken,
  readOnly = false,
  currentUserId = null,
  currentUserName = null,
  selectedNodeId = null,
  editingNodeId = null,
}) {
  const [state, setState] = useState(null);
  const [isSynced, setIsSynced] = useState(false);
  const [isLocallySynced, setIsLocallySynced] = useState(false);
  const [isNetworkSynced, setIsNetworkSynced] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [actions, setActions] = useState(null);
  const [awarenessStates, setAwarenessStates] = useState([]);
  const docRef = useRef(null);
  const providerRef = useRef(null);
  const undoManagerRef = useRef(null);

  // throttle state updates
  const pendingRef = useRef(false);

  useEffect(() => {
    if (!mindmapId) {
      docRef.current = null;
      providerRef.current = null;
      undoManagerRef.current = null;
      setState(null);
      setIsSynced(false);
      setIsLocallySynced(false);
      setIsNetworkSynced(false);
      setConnectionStatus('disconnected');
      setActions(null);
      setAwarenessStates([]);
      return;
    }

    // Reset sync flag when connecting to a (possibly new) document
    setIsSynced(false);
    setIsLocallySynced(false);
    setIsNetworkSynced(false);
    setConnectionStatus('connecting');

    const doc = new Y.Doc();
    const name = mindmapId; // IMPORTANT: keep this aligned with backend
    const wsUrl = process.env.REACT_APP_YJS_WS_URL || getDefaultWsUrl();
    const offlineDatabaseName = registerOfflineYjsDatabase(
      currentUserId,
      mindmapId
    );
    let persistence = null;
    if (offlineDatabaseName && typeof window !== 'undefined' && window.indexedDB) {
      try {
        persistence = new IndexeddbPersistence(offlineDatabaseName, doc);
      } catch {
        persistence = null;
      }
    }

    const provider = new HocuspocusProvider({
      url: wsUrl,
      name,
      document: doc,
      token: buildCollaborationCredential(authToken, getShareToken()),
      onStatus: ({ status }) => {
        setConnectionStatus(status);
        if (status !== 'connected') setIsNetworkSynced(false);
      },
      onAwarenessChange: ({ states }) => {
        setAwarenessStates(filterAwarenessStates(states));
      },
    });
    const initialAwarenessState = createAwarenessState({
      userId: currentUserId,
      displayName: currentUserName,
    });
    if (initialAwarenessState) {
      provider.awareness?.setLocalState(initialAwarenessState);
    } else {
      // Anonymous link visitors collaborate without claiming an account
      // identity or publishing a presence profile.
      provider.awareness?.setLocalState(null);
    }

    docRef.current = doc;
    providerRef.current = provider;

    const yNodes = doc.getMap('nodes');
    const yRels = doc.getMap('relationships');
    const yMeta = doc.getMap('meta');

    // Create an UndoManager for collaborative undo/redo across the
    // nodes and relationships maps. This tracks changes at the Yjs
    // level so all collaborators see a consistent history.
    const undoManager = new Y.UndoManager([yNodes, yRels]);
    undoManagerRef.current = undoManager;
    const historyActions = createYjsHistoryActions(() => undoManagerRef.current);

    const scheduleUpdate = () => {
      if (pendingRef.current) return;
      pendingRef.current = true;
      queueMicrotask(() => {
        pendingRef.current = false;
        setState(buildSnapshot(yNodes, yRels, yMeta));
      });
    };

    // observe changes
    yNodes.observe(scheduleUpdate);
    yRels.observe(scheduleUpdate);
    yMeta.observe(scheduleUpdate);

    // initial render
    setState(buildSnapshot(yNodes, yRels, yMeta));

    const acceptNetworkState = () => {
      setIsSynced(true);
      if (!initialData || readOnly) return;
      doc.transact(() => {
        if (yNodes.size !== 0 || yRels.size !== 0) return;

        // seed nodes
        if (Array.isArray(initialData.nodes)) {
          for (const node of initialData.nodes) {
            if (!node?.id) continue;
            const { id, ...rest } = stripTransientNodeFields(node);
            yNodes.set(id, rest);
          }
        }

        // seed relationships
        if (Array.isArray(initialData.relationships)) {
          for (const rel of initialData.relationships) {
            if (!rel?.parentId || !rel?.childId) continue;
            yRels.set(relKey(rel.parentId, rel.childId), {
              parentId: rel.parentId,
              childId: rel.childId,
            });
          }
        }

        // seed root only (NOT selectedId)
        if (initialData.root) yMeta.set('root', initialData.root);

        // seed any extra meta keys (avoid root/selectedId)
        if (initialData.meta && typeof initialData.meta === 'object') {
          for (const [k, v] of Object.entries(initialData.meta)) {
            if (k === 'root' || k === 'selectedId') continue;
            yMeta.set(k, v);
          }
        }
      });

      scheduleUpdate();
    };

    const onNetworkSynced = ({ state: syncedState } = {}) => {
      if (syncedState === false) return;
      setIsNetworkSynced(true);
      acceptNetworkState();
    };
    const onLocalSynced = () => {
      setIsLocallySynced(true);
      if (yNodes.size > 0 || yRels.size > 0 || yMeta.size > 0) {
        setIsSynced(true);
        scheduleUpdate();
      }
    };

    provider.on('synced', onNetworkSynced);
    persistence?.once('synced', onLocalSynced);

    // Expose a stable set of actions bound to this document.
    setActions({
      setRoot(rootId) {
        if (readOnly) return false;
        doc.transact(() => yMeta.set('root', rootId));
        return true;
      },

      setTheme(themeId) {
        if (readOnly) return false;
        doc.transact(() => {
          if (themeId) {
            yMeta.set('theme', themeId);
          } else {
            yMeta.delete('theme');
          }
        });
        return true;
      },

      addNode(node) {
        if (readOnly) return false;
        if (!node?.id) return;
        const { id, ...rest } = stripTransientNodeFields(node);
        doc.transact(() => yNodes.set(id, rest));
        return true;
      },

      updateNode(id, patch) {
        if (readOnly) return false;
        if (!id) return;
        doc.transact(() => {
          const cur = yNodes.get(id) || {};
          yNodes.set(id, stripTransientNodeFields({ ...cur, ...patch }));
        });
        return true;
      },

      deleteNode(id) {
        if (readOnly) return false;
        if (!id) return;
        doc.transact(() => {
          const toDelete = new Set();

          const collect = (nodeId) => {
            if (!nodeId || toDelete.has(nodeId)) return;
            toDelete.add(nodeId);
            for (const [, rel] of yRels.entries()) {
              if (rel?.parentId === nodeId) {
                collect(rel.childId);
              }
            }
          };

          collect(id);

          for (const nodeId of toDelete) {
            yNodes.delete(nodeId);
          }

          for (const [key, rel] of yRels.entries()) {
            if (rel && (toDelete.has(rel.parentId) || toDelete.has(rel.childId))) {
              yRels.delete(key);
            }
          }
        });
        return true;
      },

      deleteSubtree(command) {
        if (readOnly) return false;
        return applyDeleteSubtreeTransaction({
          doc,
          yNodes,
          yRels,
          undoManager: undoManagerRef.current,
          command,
        });
      },

      addRelationship(parentId, childId) {
        if (readOnly) return false;
        if (!parentId || !childId) return;
        const key = relKey(parentId, childId);
        doc.transact(() => yRels.set(key, { parentId, childId }));
        return true;
      },

      createChild(command) {
        if (readOnly) return false;
        return applyCreateNodeTransaction({
          doc,
          yNodes,
          yRels,
          undoManager: undoManagerRef.current,
          command,
        });
      },

      createNodeWithRelationship(command) {
        if (readOnly) return false;
        return applyCreateNodeTransaction({
          doc,
          yNodes,
          yRels,
          undoManager: undoManagerRef.current,
          command,
        });
      },

      beginInteraction() {
        if (readOnly) return false;
        undoManagerRef.current?.stopCapturing();
        return true;
      },

      moveNodes(command, options = {}) {
        if (readOnly) return false;
        return applyMoveNodesTransaction({
          doc,
          yNodes,
          undoManager: undoManagerRef.current,
          command,
          lastEditedBy: options.lastEditedBy || null,
          isolate: !!options.isolate,
        });
      },

      reparentSubtree(command, options = {}) {
        if (readOnly) return false;
        return applyReparentTransaction({
          doc,
          yNodes,
          yRels,
          undoManager: undoManagerRef.current,
          command,
          lastEditedBy: options.lastEditedBy || null,
        });
      },

      endInteraction() {
        if (readOnly) return false;
        undoManagerRef.current?.stopCapturing();
        return true;
      },

      deleteRelationship(parentId, childId) {
        if (readOnly) return false;
        const key = relKey(parentId, childId);
        doc.transact(() => yRels.delete(key));
        return true;
      },

      exportSnapshot() {
        return buildSnapshot(yNodes, yRels, yMeta);
      },

      ...(readOnly ? {
        undo: () => false,
        redo: () => false,
        canUndo: () => false,
        canRedo: () => false,
      } : historyActions),
    });

    return () => {
      provider.off('synced', onNetworkSynced);
      persistence?.off('synced', onLocalSynced);

      yNodes.unobserve(scheduleUpdate);
      yRels.unobserve(scheduleUpdate);
      yMeta.unobserve(scheduleUpdate);

      provider.destroy();
      doc.destroy();

      undoManagerRef.current = null;
      providerRef.current = null;
      docRef.current = null;
      setActions(null);
      setAwarenessStates([]);
    };
  }, [
    mindmapId,
    initialData,
    authToken,
    readOnly,
    currentUserId,
    currentUserName,
  ]);

  useEffect(() => {
    const presence = createAwarenessState({
      userId: currentUserId,
      displayName: currentUserName,
      selectedNodeId,
      editingNodeId,
    });
    if (!providerRef.current?.awareness) return undefined;
    if (!presence) {
      providerRef.current.awareness.setLocalState(null);
      return undefined;
    }

    const timer = setTimeout(() => {
      providerRef.current?.awareness?.setLocalState(presence);
    }, 100);
    return () => clearTimeout(timer);
  }, [
    currentUserId,
    currentUserName,
    selectedNodeId,
    editingNodeId,
    mindmapId,
  ]);

  return {
    yState: state,
    actions,
    yDoc: docRef.current,
    yProvider: providerRef.current,
    awarenessStates,
    connectionStatus,
    isLocallySynced,
    isNetworkSynced,
    isSynced,
  };
}
